import React, { Dispatch } from 'react';
import { Box, Button, Stack } from '@mui/material';
import { classes } from '../../Job/Applications/styles';
import Search from '../../SmartForms/Search';
import { ITableColumns, TemplateTableAction, TemplateTableState, IJobTemplate } from '../types';
import { ModalType, jobTemplatesActionsListItem, headerStyle } from '../config';
import { useMutation, useQuery } from '@tanstack/react-query';
import Api from '../API';
import GenericTable from '../../Components/GenericTable/GenericTable';
import { GenericTableAction } from '../../Components/GenericTable/genericTableReducer';
import JobTemplateTableRow from './JobTemplateTableRow';
import { styles } from '../styles';
import EmptyFolder from '../../../NewUI/EditJob/Questions/Modals/Icons/EmptyFolder.svg';
import GenericDialog from '../../Components/Modals/GenericDialog';
import NewJobTemplateModal from './Modals/NewJobTemplateModal';
import { ISnackbarInput } from '../../Components/CustomUIElements/StyledSnackbar';
import JobTemplateActionsPanel from './JobTemplateActionsPanel';

export default function JobTemplate({
  JobState,
  JobDispatch
}: {
  JobState: TemplateTableState<ITableColumns>;
  JobDispatch: Dispatch<TemplateTableAction>;
}) {
  const {
    data: jobTemplates,
    isLoading: loadingJobTemplates,
    refetch: refetchJobTemplates
  } = useQuery({
    queryKey: ['job templates', JobState.templateTableSettings, JobState.searchQuery],
    queryFn: async () =>
      await Api.getJobTemplates({
        page: JobState.templateTableSettings.page,
        limit: JobState.templateTableSettings.limit,
        sort: JobState.templateTableSettings.sort,
        sort_order: JobState.templateTableSettings.sort_order,
        search: JobState.searchQuery
      }),
    onError: () =>
      JobDispatch({
        type: 'SET_SNACKBAR',
        payload: { message: 'Error fetching job templates', state: 'error' }
      })
  });

  const { mutate: trashJobTemplate, isLoading: trashingJobTemplate } = useMutation({
    mutationFn: (id: string) => Api.deleteJobTemplate(id),
    onSuccess: () => {
      JobDispatch({
        type: 'SET_SNACKBAR',
        payload: { message: 'Job template trashed successfully', state: 'success' }
      });
      JobDispatch({ type: 'CLOSE_ACTION_ITEM', payload: 'delete' });
      refetchJobTemplates();
    },
    onError: () => {
      JobDispatch({
        type: 'SET_SNACKBAR',
        payload: { message: 'There was an error trashing the job template', state: 'error' }
      });
    }
  });

  const newJobTemplateModalOpen =
    JobState.modalsOpen === ModalType.NEW_JOB_TEMPLATE ||
    JobState.templateTableState.actions.isOpen.clone;

  const bulkTrashJobTemplateModalOpen = JobState.modalsOpen === ModalType.BULK_DELETE_JOB_TEMPLATES;
  const selectedItemIds = JobState.templateTableState.selectedItemIds;

  const { mutate: bulkTrashJobTemplates, isLoading: bulkTrashingJobTemplates } = useMutation({
    mutationFn: (jobTemplateIds: number[]) => Api.bulkTrashJobTemplates(jobTemplateIds),
    onSuccess: () => {
      JobDispatch({
        type: 'SET_SNACKBAR',
        payload: { message: 'Job templates trashed successfully', state: 'success' }
      });
      JobDispatch({ type: 'SET_MODALS_OPEN', payload: null });
      JobDispatch({ type: 'CLEAR_SELECTED_ITEMS' });
      refetchJobTemplates();
    },
    onError: () => {
      JobDispatch({
        type: 'SET_SNACKBAR',
        payload: { message: 'There was an error trashing the job templates', state: 'error' }
      });
    }
  });

  const trashingMessage = `${selectedItemIds.length > 1 ? 'these' : 'this'} job template${selectedItemIds.length > 1 ? 's' : ''}?`;

  return (
    <Stack sx={{ rowGap: 2, paddingTop: 5 }}>
      <Box sx={classes.applicationsContainer}>
        <JobTemplateActionsPanel {...{ JobState, JobDispatch }} />
        <Box sx={classes.filtersRow}>
          <Search
            idLabel="job-template"
            placeholder="Search job template by name"
            setSearch={(title) => JobDispatch({ type: 'SET_SEARCH_QUERY', payload: title })}
          />
          <Button
            onClick={() => {
              JobDispatch({ type: 'SET_MODALS_OPEN', payload: ModalType.NEW_JOB_TEMPLATE });
            }}
            sx={{ ...classes.newApplicationButton, marginBottom: 0.5 }}
            id="new-job-template-button"
          >
            New Template
          </Button>
        </Box>
      </Box>
      <GenericTable
        label="Question library list"
        items={(jobTemplates?.res?.job_templates || []).filter(
          (item): item is IJobTemplate => 'job_template_name' in item
        )}
        isLoadingItems={loadingJobTemplates}
        tableState={JobState.templateTableState as unknown as any}
        dispatch={JobDispatch as Dispatch<GenericTableAction<IJobTemplate>>}
        headerStyle={headerStyle}
        paginationState={JobState.templateTableSettings}
        pagination={{
          totalItems: Number(jobTemplates?.resHead['x-total-count']) || 0,
          totalPages: Number(jobTemplates?.resHead['x-total-pages']) || 0
        }}
        TableRows={JobTemplateTableRow as unknown as any}
        TableActionList={jobTemplatesActionsListItem}
        allowMultipleSelection={true}
        titleCellId="name"
        emptyMessageOverride={
          !jobTemplates?.res?.job_templates.length &&
          !JobState.searchQuery && (
            <Stack sx={styles.questionLibraryModalEmptyStateContainer}>
              <Stack component="img" src={EmptyFolder} sx={{ width: '8rem' }} />
              <Stack sx={styles.questionLibraryModalEmptyStateMessage}>
                No questions available in the library
              </Stack>
            </Stack>
          )
        }
      />
      <GenericDialog
        isDialogOpen={
          JobState.templateTableState.actions.isOpen.delete || bulkTrashJobTemplateModalOpen
        }
        setDialogOpen={() =>
          bulkTrashJobTemplateModalOpen
            ? JobDispatch({ type: 'SET_MODALS_OPEN', payload: null })
            : JobDispatch({ type: 'CLOSE_ACTION_ITEM', payload: 'delete' })
        }
        title={`Trash ${trashingMessage}`}
        description={`Are you sure you want to trash ${trashingMessage}`}
        buttonCallback={() => {
          if (bulkTrashJobTemplateModalOpen) {
            bulkTrashJobTemplates(selectedItemIds);
          } else {
            const item = JobState.templateTableState.actions.selectedItem;
            if (item) {
              trashJobTemplate(item.id.toString());
            }
          }
        }}
        callbackLoading={trashingJobTemplate || bulkTrashingJobTemplates}
        buttonText="Trash"
        secondaryButtonText="Cancel"
        url=""
      />
      {newJobTemplateModalOpen && (
        <NewJobTemplateModal
          isOpen={newJobTemplateModalOpen}
          handleClose={() => {
            JobDispatch({ type: 'SET_MODALS_OPEN', payload: null });
            JobDispatch({ type: 'CLOSE_ACTION_ITEM', payload: 'clone' });
          }}
          jobId={JobState.templateTableState.actions.selectedItem?.id}
          setSnackbar={(payload: ISnackbarInput) =>
            JobDispatch({ type: 'SET_SNACKBAR', payload: payload })
          }
          selectedTemplateName={
            'job_template_name' in (JobState.templateTableState.actions.selectedItem || {})
              ? (JobState.templateTableState.actions.selectedItem as IJobTemplate)
                  ?.job_template_name
              : undefined
          }
        />
      )}
    </Stack>
  );
}
